import { z } from "zod";
import type { ConversationRenderTurn } from "./conversationProjection";
import type { ConversationMessage } from "./conversationSchemas";
import type { ConversationCreativeMediaPart } from "./creativeMediaSchemas";
import type { CreativeMediaProgressSample } from "./creativeMediaProgress";

const legacySubmitOutputSchema = z.object({
  ok: z.literal(true), submitted: z.literal(true), job_id: z.string().regex(/^[a-zA-Z0-9_.:-]{1,200}$/u),
  status: z.enum(["draft", "queued", "generating", "validating", "uploading", "succeeded", "failed", "canceled"]),
  media_kind: z.enum(["image", "video"]), operation: z.enum(["generate", "edit", "image_to_video"]),
  provider: z.string().optional(), model: z.string().optional(),
  dispatch_status: z.enum(["triggered", "queued_for_scan"]).optional(),
  cwm_knowledge_used: z.boolean().optional(), delivery: z.string().optional(),
}).strict();

export function legacyCreativeMediaReferences(history: readonly ConversationMessage[]) {
  let turnId: string | null = null;
  const references: { jobId: string; turnId: string }[] = [];
  for (const message of history) {
    if (message.role === "user") { turnId = message.id; continue; }
    if (message.role !== "assistant" || !turnId) continue;
    for (const value of message.message.parts) {
      if (!value || typeof value !== "object" || !("type" in value) || !("output" in value) ||
          value.type !== "tool-submitCreativeMedia" || !("state" in value) || value.state !== "output-available") continue;
      const parsed = legacySubmitOutputSchema.safeParse(value.output);
      if (parsed.success) references.push({ jobId: parsed.data.job_id, turnId });
    }
  }
  return references;
}

export interface ConversationMediaSlot {
  jobId: string;
  mediaKind?: "image" | "video";
  sample: CreativeMediaProgressSample | null;
  parts: ConversationCreativeMediaPart[];
}

function stableParts(parts: readonly ConversationCreativeMediaPart[]) {
  const selected = parts.some((part) => part.data.role === "output") ? parts.filter((part) => part.data.role === "output") : parts;
  const unique = new Map<string, ConversationCreativeMediaPart>();
  for (const part of selected) unique.set(`${part.data.resource_id}:${part.data.role}`, part);
  const roleRank = { master: 0, derivative: 1, output: 2 };
  return [...unique.values()].sort((a, b) => a.data.ordinal - b.data.ordinal ||
    roleRank[a.data.role] - roleRank[b.data.role] ||
    (a.data.resource_id < b.data.resource_id ? -1 : a.data.resource_id > b.data.resource_id ? 1 : 0))
    .map((part) => ({ ...part, data: { ...part.data, presentation: unique.size > 1 ? "mosaic" as const : "single" as const } }));
}

/** 媒体归属只派生展示，不修改消息事实，也不创造 assistant。 */
export function projectConversationMediaOwnership(
  turns: readonly ConversationRenderTurn[], samples: readonly CreativeMediaProgressSample[],
  legacyReferences: readonly { jobId: string; turnId: string }[] = [],
  enabled = true,
) {
  if (!enabled) return {
    byTurn: new Map<string, ConversationMediaSlot[]>(), unassigned: [] as ConversationMediaSlot[],
    jobIds: [] as string[], turns: [...turns],
  };
  const slots = new Map<string, ConversationMediaSlot>();
  const exactOwners = new Map<string, Set<string>>();
  const firstResourceOwner = new Map<string, string>();
  const turnIds = new Set(turns.map((turn) => turn.id));
  const hint = (jobId: string, turnId: string, mediaKind?: "image" | "video") => {
    const owners = exactOwners.get(jobId) ?? new Set<string>();
    owners.add(turnId); exactOwners.set(jobId, owners);
    const slot = slots.get(jobId) ?? { jobId, sample: null, parts: [] };
    if (mediaKind) slot.mediaKind = mediaKind;
    slots.set(jobId, slot);
  };
  for (const reference of legacyReferences) if (turnIds.has(reference.turnId)) hint(reference.jobId, reference.turnId);
  for (const turn of turns) {
    for (const message of turn.assistantMessages) {
      for (const reference of message.creativeMediaJobReferences ?? []) {
        hint(reference.job_id, reference.origin_turn_id, reference.media_kind);
      }
      for (const part of message.creativeMediaParts) {
        if (part.data.job_id.startsWith("legacy:")) continue;
        const slot = slots.get(part.data.job_id) ?? { jobId: part.data.job_id, sample: null, parts: [] };
        slot.mediaKind = part.data.media_kind;
        slot.parts.push(part); slots.set(slot.jobId, slot);
        if (!firstResourceOwner.has(slot.jobId)) firstResourceOwner.set(slot.jobId, turn.id);
      }
    }
  }
  for (const sample of samples) {
    const slot = slots.get(sample.job.job_id) ?? { jobId: sample.job.job_id, sample: null, parts: [] };
    slot.sample = sample;
    slot.mediaKind = sample.job.media_kind;
    // 权威成功资源是当前 available 全集；不能让失效的历史 output 遮盖空集或 master fallback。
    slot.parts = sample.job.status === "succeeded" ? [...sample.job.resource_parts] : [];
    slots.set(slot.jobId, slot);
  }
  const byTurn = new Map<string, ConversationMediaSlot[]>();
  const unassigned: ConversationMediaSlot[] = [];
  for (const slot of slots.values()) {
    slot.parts = stableParts(slot.parts);
    const origin = slot.sample?.job.origin_turn_id;
    const hints = exactOwners.get(slot.jobId);
    const owner = origin ? (turnIds.has(origin) ? origin : null) :
      hints?.size === 1 ? [...hints][0] : hints && hints.size > 1 ? null : firstResourceOwner.get(slot.jobId);
    if (owner && turnIds.has(owner)) byTurn.set(owner, [...byTurn.get(owner) ?? [], slot]);
    else unassigned.push(slot);
  }
  return { byTurn, unassigned, jobIds: [...slots.keys()].sort(), turns: turns.map((turn) => ({
    ...turn, assistantMessages: turn.assistantMessages.map((message) => ({
      ...message, creativeMediaParts: message.creativeMediaParts.filter((part) => part.data.job_id.startsWith("legacy:")),
    })),
  })) };
}
